import { Box, Text, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import {
  BulkDestroyResultLabelConceptMappingsDocument,
  GetResultLabelConceptMappingsDocument
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { normalizeText } from 'modules/core/lib/stringUtils'
import app from 'modules/core/store/models/app'

import ImportSummaryNavigationTabs from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/_steps/_common/header'
import { WizardData } from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import { LabelToConceptId } from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import useCreateResultLabelConceptMapping from 'modules/payroll_bundle/hooks/useCreateResultLabelConceptMapping'
import { ResultLabelConceptMapping } from 'modules/payroll_bundle/hooks/useResultLabelConceptMappings'
import useUpdateResultLabelConceptMapping from 'modules/payroll_bundle/hooks/useUpdateResultLabelConceptMapping'

import MapToConcepts from './MapToConcepts'

interface Props {}

const MapToConceptsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData> & Props) => {
  const currency = data.currency
  const [getResultLabelConceptMappings] = useLazyQuery(GetResultLabelConceptMappingsDocument)
  const { create: createResultLabelConceptMapping } = useCreateResultLabelConceptMapping()
  const { update: updateResultLabelConceptMapping } = useUpdateResultLabelConceptMapping()
  const [bulkDestroyResultLabelConceptMapping] = useMutation(
    BulkDestroyResultLabelConceptMappingsDocument,
    {
      extractorFn: (dataMutation) => ({
        errors: dataMutation.payrollMutations.bulkDestroyPayrollResultLabelConceptMapping.errors
      })
    }
  )

  const onUpdateLabelToConcept = (labelToConceptId: LabelToConceptId) => {
    onDataChanged({
      ...data,
      labelToConceptId
    })
  }

  const onLoading = (loading: boolean) => {
    onDataChanged({
      ...data,
      loadingContent: loading
    })
  }

  const removedResultLabelConceptMappings = (
    labelToConceptId: LabelToConceptId | undefined,
    resultLabelConceptMappings: ResultLabelConceptMapping[]
  ) => {
    if (!labelToConceptId) return undefined

    const ids = Object.keys(labelToConceptId)
      .filter((key) => labelToConceptId[key] === undefined)
      .map((key) => resultLabelConceptMappings.find((m) => m.label === key)?.id)
      .filter((id) => id !== undefined)

    if (ids.length === 0) return undefined
    return bulkDestroyResultLabelConceptMapping({
      variables: {
        ids
      }
    })
  }

  const saveResultLabelConceptMappings = async () => {
    const resultsConcepts: Promise<UnhandledResult<Result<unknown>>>[] = []
    const result = await getResultLabelConceptMappings()
    const resultLabelConceptMappings = result.data?.payroll.resultLabelConceptMappings

    for (const label in data.labelToConceptId) {
      const conceptId = data.labelToConceptId[label]
      if (!conceptId) {
        continue
      }

      const oldMapping = resultLabelConceptMappings?.find(
        (m) => normalizeText(m.label) === normalizeText(label)
      )
      if (!oldMapping) {
        if (data?.company)
          resultsConcepts.push(
            createResultLabelConceptMapping({ companyId: data?.company.id, label, conceptId })
          )
      } else if (oldMapping.conceptId !== conceptId) {
        resultsConcepts.push(updateResultLabelConceptMapping({ id: oldMapping.id, conceptId }))
      }
    }

    if (data.labelToConceptId && resultLabelConceptMappings) {
      const removedMappings = removedResultLabelConceptMappings(
        data.labelToConceptId,
        resultLabelConceptMappings
      )
      if (removedMappings) {
        resultsConcepts.push(removedMappings)
      }
    }
    return Promise.all(resultsConcepts)
  }

  const handleNext = async () => {
    try {
      const saveResults = await saveResultLabelConceptMappings()
      saveResults.map((result) => handleResult(result, () => null))
    } catch (err) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error')
      })
      return Promise.reject()
    }

    track(trackingEvents.payroll.continueToFinalResultImportStep, {
      period_status: data.payrollPolicyPeriod.status,
      source: data.source
    })
  }

  useOnNext(handleNext)

  return (
    <Box gap="s24" overflowY="hidden">
      <ImportSummaryNavigationTabs selectedStep={1} />

      <Text.Normal>
        {i18n.t('payroll_bundle.payroll_summary_import.wizard.map_to_concepts_step.description')}
      </Text.Normal>

      {currency && data.payrollSummaryImport && (
        <MapToConcepts
          currency={currency}
          payrollSummaryImportId={data.payrollSummaryImport.id}
          onUpdateLabelToConcept={onUpdateLabelToConcept}
          initialLabelToConceptId={data.labelToConceptId}
          onLoading={onLoading}
        />
      )}
    </Box>
  )
}

export default MapToConceptsStep
